/**
 * GamePage - Dedicated page for viewing game details.
 *
 * Route: /admin/games/:gameId
 */

import { useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Breadcrumbs,
  Link,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import BackIcon from '@mui/icons-material/ArrowBack';
import ScheduledIcon from '@mui/icons-material/Schedule';
import InProgressIcon from '@mui/icons-material/PlayArrow';
import FinishedIcon from '@mui/icons-material/CheckCircle';
import CancelledIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import CopyIcon from '@mui/icons-material/ContentCopy';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import TelegramIcon from '@mui/icons-material/Telegram';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { gamesApi, type UpdateScoresRequest } from '@/api/client';
import { EventLog } from '@/components/EventLog';
import { formatTime } from '@/lib/i18n';
import type { Game, GameStatus } from '@/types';
import { getGameStatus, generatePaperId } from '@/types';

/**
 * Convert backend score to kyykkä display score.
 * Score = remaining akka × 2 + remaining pappi (lower is better).
 */
function toDisplayScore(backendScore: number | null): number | null {
  if (backendScore === null) return null;
  return backendScore;
}

/**
 * Convert total game score to kyykkä display score.
 */
function toTotalDisplayScore(backendScore: number | null): number | null {
  if (backendScore === null) return null;
  return backendScore;
}

// Status chip configuration
const STATUS_CONFIG: Record<GameStatus, { color: 'default' | 'primary' | 'success' | 'error'; icon: React.ReactNode; label: string }> = {
  scheduled: { color: 'default', icon: <ScheduledIcon />, label: 'Scheduled' },
  in_progress: { color: 'primary', icon: <InProgressIcon />, label: 'In Progress' },
  finished: { color: 'success', icon: <FinishedIcon />, label: 'Finished' },
  cancelled: { color: 'error', icon: <CancelledIcon />, label: 'Cancelled' },
};

// Format date
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric', year: 'numeric' });
}

export function GamePage() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const gameIdNum = gameId ? parseInt(gameId, 10) : null;

  // Score edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editScores, setEditScores] = useState<{
    team1_1st: number | null;
    team2_1st: number | null;
    team1_2nd: number | null;
    team2_2nd: number | null;
    team1_ot: number | null;
    team2_ot: number | null;
  }>({
    team1_1st: null,
    team2_1st: null,
    team1_2nd: null,
    team2_2nd: null,
    team1_ot: null,
    team2_ot: null,
  });

  const handleTeamClick = (teamId: number | null) => {
    if (teamId) {
      navigate(`/admin/teams/${teamId}`);
    }
  };

  const handleBlockClick = (paperId: number) => {
    navigate(`/admin/blocks/${paperId}`);
  };

  const handleCourtClick = (courtId: number) => {
    navigate(`/admin/courts/${courtId}`);
  };

  const handleRefereeClick = (refereeId: number | null) => {
    if (refereeId) {
      navigate(`/admin/referees/${refereeId}`);
    }
  };

  const handleCopyText = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Fetch game details
  const {
    data: game,
    isLoading,
    error,
  } = useQuery<Game>({
    queryKey: ['game', gameIdNum],
    queryFn: () => gamesApi.get(gameIdNum!),
    enabled: gameIdNum !== null,
  });

  // Update scores mutation
  const updateScoresMutation = useMutation({
    mutationFn: (data: UpdateScoresRequest) => gamesApi.updateScores(gameIdNum!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['game', gameIdNum] });
      setEditModalOpen(false);
    },
  });

  // Open edit modal
  const openEditModal = () => {
    if (game) {
      setEditScores({
        team1_1st: game.team1_1st_score,
        team2_1st: game.team2_1st_score,
        team1_2nd: game.team1_2nd_score,
        team2_2nd: game.team2_2nd_score,
        team1_ot: game.team1_ot_score,
        team2_ot: game.team2_ot_score,
      });
      setEditModalOpen(true);
    }
  };

  // Handle save scores
  const handleSaveScores = () => {
    // Only include OT scores if at least one is non-zero (actual overtime)
    const hasOvertimeScores = (editScores.team1_ot !== null && editScores.team1_ot !== 0) ||
                              (editScores.team2_ot !== null && editScores.team2_ot !== 0);

    updateScoresMutation.mutate({
      team1_1st_score: editScores.team1_1st,
      team2_1st_score: editScores.team2_1st,
      team1_2nd_score: editScores.team1_2nd,
      team2_2nd_score: editScores.team2_2nd,
      // Only send OT scores if there are actual overtime values
      ...(hasOvertimeScores ? {
        team1_ot_score: editScores.team1_ot,
        team2_ot_score: editScores.team2_ot,
      } : {}),
    });
  };

  if (!gameIdNum) {
    return (
      <Box>
        <Alert severity="error">Invalid game ID</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/games')} sx={{ mt: 2 }}>
          Back to Games
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/admin" underline="hover" color="inherit">
          Admin
        </Link>
        <Link component={RouterLink} to="/admin/games" underline="hover" color="inherit">
          Games
        </Link>
        <Typography color="text.primary">Game {gameId}</Typography>
      </Breadcrumbs>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load game details
          </Alert>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/games')}>
            Back to Games
          </Button>
        </Box>
      ) : game ? (
        <>
          {/* Header with Referee Info */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h4" fontWeight={700}>
                Game Details
              </Typography>
              {(() => {
                const status = getGameStatus(game);
                const config = STATUS_CONFIG[status];
                return (
                  <Chip
                    icon={config.icon as React.ReactElement}
                    label={config.label}
                    color={config.color}
                  />
                );
              })()}
            </Box>

            {/* Referee Info - Top Right */}
            {game.referee ? (
              <Paper variant="outlined" sx={{ p: 1.5, minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Referee
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <PersonIcon color="action" fontSize="small" />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="primary.main"
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() => handleRefereeClick(game.referee)}
                  >
                    {game.referee_name || `#${game.referee}`}
                  </Typography>
                </Box>
                {game.referee_phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <PhoneIcon color="action" fontSize="small" />
                    <Typography variant="body2">{game.referee_phone}</Typography>
                    <Tooltip title="Copy phone">
                      <IconButton size="small" onClick={() => handleCopyText(game.referee_phone!)}>
                        <CopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                {game.referee_telegram && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TelegramIcon color="action" fontSize="small" />
                    <Typography variant="body2">
                      {game.referee_telegram.startsWith('@') ? game.referee_telegram : `@${game.referee_telegram}`}
                    </Typography>
                    <Tooltip title="Copy telegram">
                      <IconButton size="small" onClick={() => handleCopyText(game.referee_telegram!)}>
                        <CopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ p: 1.5, minWidth: 200, bgcolor: 'action.hover' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  No referee assigned
                </Typography>
              </Paper>
            )}
          </Box>

          {/* Teams Section */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Teams
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center', flex: 1, minWidth: 200 }}>
                <Typography
                  variant="h5"
                  fontWeight={game.winner_id === game.team1 ? 700 : 400}
                  color={game.winner_id === game.team1 ? 'success.main' : 'primary.main'}
                  sx={{
                    cursor: game.team1 ? 'pointer' : 'default',
                    '&:hover': game.team1 ? { textDecoration: 'underline' } : {},
                  }}
                  onClick={() => handleTeamClick(game.team1)}
                >
                  {game.team1_name || 'TBD'}
                </Typography>
                {game.winner_id === game.team1 && (
                  <Chip label="Winner" color="success" size="small" sx={{ mt: 1 }} />
                )}
              </Box>
              
              <Typography variant="h4" color="text.secondary" fontWeight={300}>
                vs
              </Typography>
              
              <Box sx={{ textAlign: 'center', flex: 1, minWidth: 200 }}>
                <Typography
                  variant="h5"
                  fontWeight={game.winner_id === game.team2 ? 700 : 400}
                  color={game.winner_id === game.team2 ? 'success.main' : 'primary.main'}
                  sx={{
                    cursor: game.team2 ? 'pointer' : 'default',
                    '&:hover': game.team2 ? { textDecoration: 'underline' } : {},
                  }}
                  onClick={() => handleTeamClick(game.team2)}
                >
                  {game.team2_name || 'TBD'}
                </Typography>
                {game.winner_id === game.team2 && (
                  <Chip label="Winner" color="success" size="small" sx={{ mt: 1 }} />
                )}
              </Box>
            </Box>

            {game.is_tie && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Chip label="Tie" color="warning" />
              </Box>
            )}
          </Paper>

          {/* Score Section */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Score
              </Typography>
              {getGameStatus(game) !== 'scheduled' && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={openEditModal}
                >
                  Edit Scores
                </Button>
              )}
            </Box>
            {getGameStatus(game) !== 'scheduled' ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h3" fontFamily="monospace" fontWeight={700}>
                    {toTotalDisplayScore(game.team1_score) ?? '-'}
                  </Typography>
                  <Typography variant="h3" color="text.secondary">
                    -
                  </Typography>
                  <Typography variant="h3" fontFamily="monospace" fontWeight={700}>
                    {toTotalDisplayScore(game.team2_score) ?? '-'}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
                  {game.team1_1st_score !== null && game.team2_1st_score !== null && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        1st Half
                      </Typography>
                      <Typography variant="h6" fontFamily="monospace">
                        {toDisplayScore(game.team1_1st_score)} - {toDisplayScore(game.team2_1st_score)}
                      </Typography>
                    </Box>
                  )}
                  {game.team1_2nd_score !== null && game.team2_2nd_score !== null && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        2nd Half
                      </Typography>
                      <Typography variant="h6" fontFamily="monospace">
                        {toDisplayScore(game.team1_2nd_score)} - {toDisplayScore(game.team2_2nd_score)}
                      </Typography>
                    </Box>
                  )}
                  {game.team1_ot_score !== null && game.team2_ot_score !== null && game.game_ot && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Overtime
                      </Typography>
                      <Typography variant="h6" fontFamily="monospace" color="warning.main">
                        {toDisplayScore(game.team1_ot_score)} - {toDisplayScore(game.team2_ot_score)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            ) : (
              <Typography color="text.secondary" textAlign="center">
                Game not started
              </Typography>
            )}
          </Paper>

          {/* Game Info Section */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Game Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Block
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  color="primary.main"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                  onClick={() => handleBlockClick(game.paper_id)}
                >
                  {generatePaperId(game.paper_id)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  League
                </Typography>
                <Chip
                  label={game.league_id.toUpperCase()}
                  size="small"
                  color={game.league_id === 'yle' ? 'primary' : game.league_id === 'pro' ? 'secondary' : 'default'}
                  variant="outlined"
                />
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Round
                </Typography>
                <Typography variant="body1">
                  {game.round}
                  {game.round_name && (
                    <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>
                      ({game.round_name.replace('_', ' ')})
                    </Typography>
                  )}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Court
                </Typography>
                {game.court_id ? (
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="primary.main"
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() => handleCourtClick(game.court_id!)}
                  >
                    {game.court_id}
                  </Typography>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Not assigned
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Schedule Section */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Schedule
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Scheduled Start
                </Typography>
                <Typography variant="body1">
                  {formatTime(game.schedule_start)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(game.schedule_start)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Scheduled End
                </Typography>
                <Typography variant="body1">
                  {formatTime(game.schedule_end)}
                </Typography>
              </Box>

              {game.time_start && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Actual Start
                  </Typography>
                  <Typography variant="body1" color="info.main">
                    {formatTime(game.time_start)}
                  </Typography>
                </Box>
              )}

              {game.time_end && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Actual End
                  </Typography>
                  <Typography variant="body1" color="info.main">
                    {formatTime(game.time_end)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Game State Flags (for debugging) */}
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              State Flags
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label="Started"
                size="small"
                color={game.game_start ? 'success' : 'default'}
                variant={game.game_start ? 'filled' : 'outlined'}
              />
              <Chip
                label="1st Half"
                size="small"
                color={game.game_1st ? 'success' : 'default'}
                variant={game.game_1st ? 'filled' : 'outlined'}
              />
              <Chip
                label="2nd Half"
                size="small"
                color={game.game_2nd ? 'success' : 'default'}
                variant={game.game_2nd ? 'filled' : 'outlined'}
              />
              <Chip
                label="Overtime"
                size="small"
                color={game.game_ot ? 'warning' : 'default'}
                variant={game.game_ot ? 'filled' : 'outlined'}
              />
              <Chip
                label="Ended"
                size="small"
                color={game.game_end ? 'success' : 'default'}
                variant={game.game_end ? 'filled' : 'outlined'}
              />
              <Chip
                label="Cancelled"
                size="small"
                color={game.game_cancel ? 'error' : 'default'}
                variant={game.game_cancel ? 'filled' : 'outlined'}
              />
            </Box>
          </Paper>

          {/* Event Log */}
          <EventLog params={{ game_id: game.id }} title="Game Event Log" />

          {/* Edit Scores Modal */}
          <Dialog
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Edit Scores</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                {/* Team names header */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr', gap: 2, alignItems: 'center' }}>
                  <Box />
                  <Typography variant="subtitle2" textAlign="center">
                    {game.team1_name || 'Team 1'}
                  </Typography>
                  <Typography variant="subtitle2" textAlign="center">
                    {game.team2_name || 'Team 2'}
                  </Typography>
                </Box>

                {/* 1st Half */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    1st Half
                  </Typography>
                  <TextField
                    type="number"
                    value={editScores.team1_1st !== null ? editScores.team1_1st : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      const num = val === '' ? null : parseInt(val, 10);
                      setEditScores((prev) => ({
                        ...prev,
                        team1_1st: isNaN(num as number) ? null : num,
                      }));
                    }}
                    size="small"
                    inputProps={{ min: 0 }}
                  />
                  <TextField
                    type="number"
                    value={editScores.team2_1st !== null ? editScores.team2_1st : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      const num = val === '' ? null : parseInt(val, 10);
                      setEditScores((prev) => ({
                        ...prev,
                        team2_1st: isNaN(num as number) ? null : num,
                      }));
                    }}
                    size="small"
                    inputProps={{ min: 0 }}
                  />
                </Box>

                {/* 2nd Half */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    2nd Half
                  </Typography>
                  <TextField
                    type="number"
                    value={editScores.team1_2nd !== null ? editScores.team1_2nd : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      const num = val === '' ? null : parseInt(val, 10);
                      setEditScores((prev) => ({
                        ...prev,
                        team1_2nd: isNaN(num as number) ? null : num,
                      }));
                    }}
                    size="small"
                    inputProps={{ min: 0 }}
                  />
                  <TextField
                    type="number"
                    value={editScores.team2_2nd !== null ? editScores.team2_2nd : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      const num = val === '' ? null : parseInt(val, 10);
                      setEditScores((prev) => ({
                        ...prev,
                        team2_2nd: isNaN(num as number) ? null : num,
                      }));
                    }}
                    size="small"
                    inputProps={{ min: 0 }}
                  />
                </Box>

                {/* Overtime */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Overtime
                  </Typography>
                  <TextField
                    type="number"
                    value={editScores.team1_ot !== null ? editScores.team1_ot : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      const num = val === '' ? null : parseInt(val, 10);
                      setEditScores((prev) => ({
                        ...prev,
                        team1_ot: isNaN(num as number) ? null : num,
                      }));
                    }}
                    size="small"
                    inputProps={{ min: 0 }}
                    placeholder="(optional)"
                  />
                  <TextField
                    type="number"
                    value={editScores.team2_ot !== null ? editScores.team2_ot : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      const num = val === '' ? null : parseInt(val, 10);
                      setEditScores((prev) => ({
                        ...prev,
                        team2_ot: isNaN(num as number) ? null : num,
                      }));
                    }}
                    size="small"
                    inputProps={{ min: 0 }}
                    placeholder="(optional)"
                  />
                </Box>
              </Box>

              {updateScoresMutation.isError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Failed to update scores
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSaveScores}
                disabled={updateScoresMutation.isPending}
              >
                {updateScoresMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : null}
    </Box>
  );
}

